import { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  IOperationItem,
  TMenhirOperationCategory,
  TMenhirOperationStatus,
} from '@/common/integrations/menhir/employee/operations/operations.types';
import {
  isESChildcareOperationItem,
  isExpenseOperationItem,
  isTopUpOperationItem,
} from '@/common/utils/operations';
import { BenefitIcon } from '@/web/components/BenefitIcon/BenefitIcon';
import {
  Badge,
  IconBan,
  IconRightLeft,
  IconSack,
  themeVars,
} from '@coverflex-tech/hypernova';
import { MoneyUtils } from '@coverflex-tech/hypernova-utils';
import { TFunction, i18n } from 'i18next';

export interface ItemProps {
  icon: ReactNode;
  description: ReactNode;
  onClick?: () => void;
  badge?: ReactNode;
}

const getOperationIcon = (operation: IOperationItem) => {
  const iconColor = operation.isDebit
    ? themeVars.color.semantic.text.accentSecondary10
    : themeVars.color.semantic.text.systemSuccess;

  if (operation.status === 'rejected') {
    return <IconBan color={themeVars.color.semantic.text.systemError} />;
  }

  if (isTopUpOperationItem(operation)) {
    return <IconSack color={iconColor} />;
  }

  if (!operation?.categorySlug) {
    return <IconRightLeft color={iconColor} />;
  }

  return <BenefitIcon benefit={operation.categorySlug} color={iconColor} />;
};

export const getBadge = (
  operation: IOperationItem,
  t: TFunction,
  size?: 'md' | 'lg'
) => {
  if (operation.status === 'pending') {
    return (
      <Badge status="info" emphasis="soft">
        {t('general.status.pending')}
      </Badge>
    );
  }

  if (
    operation.status === 'on_hold' ||
    operation.status === 'pending_approval'
  ) {
    return (
      <Badge status="info" size={size} emphasis="soft">
        {t(`dashboard.reimbursement_details.status_badge.${operation.status}`)}
      </Badge>
    );
  }
  if (operation.status === 'cancelled') {
    return (
      <Badge status="disabled" size={size} emphasis="soft">
        {t('dashboard.reimbursement_details.status_badge.cancelled')}
      </Badge>
    );
  }

  const rejectionReasonParam = operation.descriptionParams.find(
    (param) => param.key === 'rejection_reason'
  );
  if (operation.status === 'rejected' && !rejectionReasonParam) {
    return (
      <Badge status="error" size={size} emphasis="soft">
        {t('dashboard.reimbursement_details.status_badge.rejected')}
      </Badge>
    );
  }

  if (operation.status === 'rejected') {
    const rejectionReason =
      operation.descriptionParams.find(
        (param) => param.key === 'rejection_reason'
      )?.value ?? 'generic_error';

    return (
      <Badge status="error" size={size} emphasis="soft">
        {t(`rejection_reason.${rejectionReason}.badge`)}
      </Badge>
    );
  }

  if (operation.status === 'confirmed') {
    if (isESChildcareOperationItem(operation)) {
      return (
        <Badge status="success" size={size} emphasis="soft">
          {t('general.status.paid')}
        </Badge>
      );
    }

    return (
      <Badge status="success" size={size} emphasis="soft">
        {t('dashboard.reimbursement_details.status_badge.approved')}
      </Badge>
    );
  }

  return null;
};

export const getReimbursementBadge = (
  status: TMenhirOperationStatus,
  t: TFunction,
  size?: 'md' | 'lg'
) => {
  if (status === 'pending') {
    return <Badge status="info">{t('general.status.pending')}</Badge>;
  }

  if (
    status === 'on_hold' ||
    status === 'pending_approval' ||
    status === 'processing_approval'
  ) {
    return (
      <Badge status="info" size={size}>
        {t(`dashboard.reimbursement_details.status_badge.${status}`)}
      </Badge>
    );
  }
  if (status === 'cancelled') {
    return (
      <Badge status="disabled" size={size}>
        {t('dashboard.reimbursement_details.status_badge.cancelled')}
      </Badge>
    );
  }
  if (status === 'rejected') {
    return (
      <Badge status="error" size={size}>
        {t('dashboard.reimbursement_details.status_badge.rejected')}
      </Badge>
    );
  }

  if (status === 'confirmed' || status === 'approved') {
    return (
      <Badge status="success" size={size}>
        {t('dashboard.reimbursement_details.status_badge.approved')}
      </Badge>
    );
  }

  return null;
};

export const getOperationDescription = (
  operation: IOperationItem,
  { t, i18n }: { t: TFunction; i18n: i18n }
) => {
  if (i18n.exists(operation.descriptionTag)) {
    const params = operation.descriptionParams.reduce((acc, val) => {
      if (val.type === 'money') {
        return {
          ...acc,
          [val.key]: MoneyUtils.moneyToString(val.value),
        };
      }
      if (val.key === 'category') {
        return {
          ...acc,
          [val.key]: t(`dashboard.benefits.links.${val.value}`),
        };
      }
      if (val.key === 'beneficiary' && val.value === null) {
        val.value = t('dashboard.family_members.me');
      }
      if (val.key === 'merchant_name' && val.value) {
        val.value = val.value.toString().toLowerCase();
      }
      return { ...acc, [val.key]: val.value };
    }, {});

    return <Trans i18nKey={operation.descriptionTag} values={params} />;
  } else if (i18n.exists(`operations.generic.${operation.type}`)) {
    return t(`operations.generic.${operation.type}`);
  }

  return t('operations.unknown_label');
};

export const useItemProps = () => {
  const { t, i18n } = useTranslation();

  return {
    getItemProps: (operation: IOperationItem): ItemProps => {
      const icon = getOperationIcon(operation);
      const description = getOperationDescription(operation, { t, i18n });
      const badge =
        isExpenseOperationItem(operation) &&
        (operation.categorySlug as TMenhirOperationCategory | 'health') !==
          'health'
          ? getBadge(operation, t)
          : undefined;

      return {
        icon,
        badge,
        description,
      };
    },
  };
};
